import React, { useEffect } from 'react';
import { TextField, MenuItem, Box, Typography } from '@mui/material';
import Material from './Material';

const StepThree = ({ supplierData, onSupplierDataChange }) => {
  
  // Effect to ensure at least 1 Module Rating exists by default on load
  useEffect(() => {
    let changed = false;
    const updated = supplierData.map((s) => {
      const details = s.details || {};
      // If no ratings or wattage data exists, initialize it to 1
      if (!details.moduleRatings || !details.moduleWattageData || details.moduleWattageData.length === 0) {
        changed = true;
        return {
          ...s,
          details: {
            ...details,
            moduleRatings: details.moduleRatings || "1",
            moduleWattageData: details.moduleWattageData?.length > 0 
              ? details.moduleWattageData 
              : [{ moduleWattageWp: '', moduleWattageNos: '', binningOption: '' }]
          }
        };
      }
      return s;
    });

    if (changed) {
      onSupplierDataChange(updated);
    }
  }, []); // Run once on mount

  const handleChange = (sIdx, field, value, wIdx = null) => {
    const updated = [...supplierData];
    if (!updated[sIdx].details) updated[sIdx].details = {};

    if (wIdx !== null) {
      // Logic for Wattage Data fields
      if (!updated[sIdx].details.moduleWattageData) updated[sIdx].details.moduleWattageData = [];
      if (!updated[sIdx].details.moduleWattageData[wIdx]) {
        updated[sIdx].details.moduleWattageData[wIdx] = {};
      }
      updated[sIdx].details.moduleWattageData[wIdx][field] = value;
    } else if (field === 'moduleRatings') {
      // IMMEDIATE UPDATE LOGIC
      const num = Math.max(1, Math.min(5, parseInt(value, 10) || 0));
      updated[sIdx].details[field] = value; // Keep the raw value in the text field

      // Only update the array if the number is valid (1-5)
      if (num >= 1) {
        const current = updated[sIdx].details.moduleWattageData || [];
        updated[sIdx].details.moduleWattageData = Array.from({ length: num }, (_, i) =>
          current[i] || { moduleWattageWp: '', moduleWattageNos: '', binningOption: '' }
        );
      }
    } else {
      // Logic for standard detail fields
      updated[sIdx].details[field] = value;
    }
    onSupplierDataChange(updated);
  };

  const handleMaterialChange = (sIdx, materials) => {
    const updated = [...supplierData];
    if (!updated[sIdx].details) updated[sIdx].details = {};
    updated[sIdx].details.materials = materials;
    onSupplierDataChange(updated);
  };

  const renderBinningFields = (binning, wattage, sIdx, wIdx) => {
    const count = parseInt(binning || '0', 10);
    const fields = [];
    for (let i = 1; i <= count; i++) {
      fields.push(
        <Box key={i} display="grid" mt={2} gridTemplateColumns="repeat(2, 1fr)" gap={2}>
          <TextField
            label={`I${i}: Current Binning`}
            value={wattage?.[`i${i}CurrentBinning`] || ''}
            onChange={(e) => handleChange(sIdx, `i${i}CurrentBinning`, e.target.value, wIdx)}
            fullWidth
          />
          <TextField
            label={`I${i} Storage Location`}
            value={wattage?.[`i${i}Location`] || ''}
            onChange={(e) => handleChange(sIdx, `i${i}Location`, e.target.value, wIdx)}
            fullWidth
          />
        </Box>
      );
    }
    return fields;
  };

  return (
    <div>
      {supplierData.map((supplier, sIdx) => {
        const d = supplier.details || {};
        return (
          <Box key={sIdx} mb={4} p={2} borderRadius="8px"
            className="bg-gray-50 p-4 rounded-lg mt-3"
            style={{ boxShadow: 'inset 0px 0px 4px 2px rgb(0 0 0 / 0.05)' }}>
            <Typography variant="h6" gutterBottom>
              {`Supplier: ${supplier.name}`}
            </Typography>

            <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
              <TextField select label="Module Type"
                value={d.moduleType || ''} 
                onChange={(e) => handleChange(sIdx, 'moduleType', e.target.value)} fullWidth>
                {['Mono-Facial', 'Bi-Facial'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
              <TextField label="Cell Size" value={d.cellSize || ''} onChange={(e) => handleChange(sIdx, 'cellSize', e.target.value)} fullWidth />
              <TextField label="Cell Type" value={d.cellType || ''} onChange={(e) => handleChange(sIdx, 'cellType', e.target.value)} fullWidth />
              <TextField label="Number of Cells" value={d.numberOfCells || ''} onChange={(e) => handleChange(sIdx, 'numberOfCells', e.target.value)} fullWidth />
              <TextField label="Module Size" value={d.moduleSize || ''} onChange={(e) => handleChange(sIdx, 'moduleSize', e.target.value)} fullWidth />
              <TextField label="Mounting Hole Location" value={d.mountingHoles || ''} onChange={(e) => handleChange(sIdx, 'mountingHoles', e.target.value)} fullWidth />
              <TextField label="Module Technology" value={d.moduleTechnology || ''} onChange={(e) => handleChange(sIdx, 'moduleTechnology', e.target.value)} fullWidth />
              
              <TextField 
                label="Number of Module Ratings" 
                type="number"
                value={d.moduleRatings || ''}
                onChange={(e) => handleChange(sIdx, 'moduleRatings', e.target.value)}
                InputProps={{ inputProps: { min: 1, max: 5 } }} 
                fullWidth 
              />
            </Box>

            {(d.moduleWattageData || []).map((w, wIdx) => (
              <Box key={wIdx} mt={4} p={2} border="1px solid #ccc" borderRadius="8px">
                <Typography variant="subtitle1" gutterBottom>
                  {`Module Wattage ${wIdx + 1}`}
                </Typography>
                <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
                  <TextField label="Module Wattage (Wp)" value={w.moduleWattageWp || ''} 
                    onChange={(e) => handleChange(sIdx, 'moduleWattageWp', e.target.value, wIdx)} fullWidth />
                  <TextField label="Module Wattage (Nos)" value={w.moduleWattageNos || ''} 
                    onChange={(e) => handleChange(sIdx, 'moduleWattageNos', e.target.value, wIdx)} fullWidth />
                  <TextField select label="Binning Option" value={w.binningOption || ''} 
                    onChange={(e) => handleChange(sIdx, 'binningOption', e.target.value, wIdx)} fullWidth>
                    {['1','2','3'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </TextField>
                </Box>
                {renderBinningFields(w.binningOption, w, sIdx, wIdx)}
              </Box>
            ))}

            <details className="bg-gray-100 p-4 rounded-lg mt-3"
              style={{ boxShadow: 'inset 0px 0px 4px 2px rgb(0 0 0 / 0.05)' }}>
              <summary className="text-xl text-[#424752] cursor-pointer">Materials</summary>
              <Box mt={4} p={2} border="1px solid #ccc" borderRadius="8px">
                <Material
                  materials={d.materials || []}
                  onMaterialsChange={(m) => handleMaterialChange(sIdx, m)}
                />
              </Box>
            </details>
          </Box>
        );
      })}
    </div>
  );
};

export default StepThree;